// The pipeline's small shared parts (spec 09 §4.14): the tab strip the board,
// the list and the forecast share, the owner and customer filters the three
// pages carry across it, a deal's status and stage chips, and the next-step
// cell the board card, the deal list, the deal page and the customer book all
// print (§3.12).
//
// THE TAB STRIP IS `NavTabs`, a strip of buttons that navigate. A link strip
// (`RouterTabs`) would drop the filters, because its links carry no search.
// The strip carries the two filters the three pages share, under the list's
// own keys (`PIPELINE_SHARED_FILTER_KEYS`), so a reader who narrowed the board
// to one customer opens the list and the forecast narrowed the same way.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { Field } from "../../../../../components/field";
import { RefLabel } from "../../../../../components/ref-label";
import { Select } from "../../../../../components/select";
import { StatusChip, type ChipVariant } from "../../../../../components/status-chip";
import { NavTabs } from "../../../../../components/tabs";
import { formatBusinessDate } from "../../../../../lib/datetime";
import { useDebouncedValue } from "../../../../../lib/use-debounced-value";
import { useCustomer360, useCustomers } from "../../hooks/sales-queries";
import {
  dealStatusLabel,
  nextStepOf,
  taskKindLabel,
  type DealStatus,
  type NextActivityWire,
} from "../../lib/pipeline";
import { compactSearch } from "../../screens/search-params";
import { canOpenAnalytics, useSalesPermissions } from "../../hooks/permissions";

export type PipelinePage = "board" | "list" | "forecast" | "analytics";

/** The two filters the three pages share. */
export interface SharedPipelineFilters {
  readonly owner?: string | undefined;
  readonly customer?: string | undefined;
}

export function PipelineTabs({
  active,
  shared,
}: {
  readonly active: PipelinePage;
  readonly shared: SharedPipelineFilters;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const search = compactSearch({ owner: shared.owner, customer: shared.customer });
  // ANALYTICS IS THE STRIP'S FOURTH TAB (D-445), offered on §4.9's own
  // audience. It takes none of the shared filters: §4.9 narrows by its own
  // range and tier, and a manager's own row by the server.
  const analytics = canOpenAnalytics(perms);
  return (
    <NavTabs
      label={t("sales.pipeline.tabsAria")}
      active={active}
      data-testid="sales-pipeline-tabs"
      tabs={[
        {
          id: "board",
          label: t("sales.pipeline.tabs.board"),
          "data-testid": "sales-pipeline-tab-board",
        },
        {
          id: "list",
          label: t("sales.pipeline.tabs.list"),
          "data-testid": "sales-pipeline-tab-list",
        },
        {
          id: "forecast",
          label: t("sales.pipeline.tabs.forecast"),
          "data-testid": "sales-pipeline-tab-forecast",
        },
        ...(analytics
          ? [
              {
                id: "analytics",
                label: t("sales.nav.analytics"),
                "data-testid": "sales-pipeline-tab-analytics",
              },
            ]
          : []),
      ]}
      onSelect={(id) => {
        if (id === active) return;
        if (id === "board") void navigate({ to: "/sales/pipeline", search });
        else if (id === "list") void navigate({ to: "/sales/opportunities", search });
        else if (id === "analytics") void navigate({ to: "/sales/analytics" });
        else void navigate({ to: "/sales/forecast", search });
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

export function dealStatusVariant(status: DealStatus): ChipVariant {
  return status === "won" ? "ok" : status === "lost" ? "neutral" : "accent";
}

export function DealStatusChip({ status }: { readonly status: DealStatus }) {
  const { t } = useTranslation();
  return (
    <StatusChip
      variant={dealStatusVariant(status)}
      data-testid="sales-deal-status"
      data-status={status}
    >
      {dealStatusLabel(t, status)}
    </StatusChip>
  );
}

/** "Negotiation · 75%": the stage in the reader's language beside the
 * probability the forecast weighs it by. */
export function StageText({ name, pct }: { readonly name: string; readonly pct: number }) {
  const { t } = useTranslation();
  return (
    <span data-testid="sales-deal-stage">
      {t("sales.pipeline.stageWithProbability", { stage: name, pct })}
    </span>
  );
}

/** The idle chip: whole days in the stage, red once the deal rots. A deal
 * that does not rot shows the days as plain text, so red means one thing. */
export function IdleChip({ days, rotting }: { readonly days: number; readonly rotting: boolean }) {
  const { t } = useTranslation();
  return rotting ? (
    <StatusChip variant="bad" data-testid="sales-deal-idle" data-rotting="true">
      {t("sales.pipeline.rotting", { count: days })}
    </StatusChip>
  ) : (
    <span className="num" data-testid="sales-deal-idle" data-rotting="false">
      {t("sales.pipeline.inStageDays", { count: days })}
    </span>
  );
}

// ---------------------------------------------------------------------------
// The next step (§3.12)
// ---------------------------------------------------------------------------

/**
 * A deal's or a customer's next step: the earliest open task's kind and date,
 * amber when it is due today and red when it is overdue, with the words
 * saying so beside the colour; or the flag "No next step" with Schedule when
 * `onSchedule` is given. It never shows a task's words or its assignee.
 */
export function NextStepCell({
  next,
  needsNextStep,
  onSchedule,
  scheduleName,
}: {
  readonly next: NextActivityWire | null | undefined;
  readonly needsNextStep: boolean;
  readonly onSchedule?: (() => void) | undefined;
  /** The deal's or customer's name, for the Schedule button's full label. */
  readonly scheduleName?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const step = nextStepOf(next, needsNextStep);
  const schedule =
    onSchedule === undefined ? null : (
      <Button
        size="sm"
        data-testid="sales-next-step-schedule"
        aria-label={
          scheduleName === undefined
            ? undefined
            : t("sales.nextStep.scheduleAria", { name: scheduleName })
        }
        onClick={(event) => {
          event.stopPropagation();
          onSchedule();
        }}
      >
        {t("sales.nextStep.schedule")}
      </Button>
    );
  if (step.kind === "none") return null;
  if (step.kind === "flag") {
    return (
      <span className="sales-next-step" data-testid="sales-next-step" data-band="none">
        <StatusChip variant="warn" dot={false} data-testid="sales-next-step-flag">
          {t("sales.nextStep.none")}
        </StatusChip>
        {schedule}
      </span>
    );
  }
  const kind = taskKindLabel(t, step.taskKind);
  const date = formatBusinessDate(step.dueOn, i18n.language);
  if (step.band === "ahead") {
    return (
      <span className="sales-next-step" data-testid="sales-next-step" data-band="ahead">
        <span className="num">{t("sales.nextStep.on", { kind, date })}</span>
      </span>
    );
  }
  return (
    <span className="sales-next-step" data-testid="sales-next-step" data-band={step.band}>
      <StatusChip variant={step.band === "overdue" ? "bad" : "warn"}>
        {t("sales.nextStep.on", {
          kind,
          date:
            step.band === "overdue"
              ? t("sales.nextStep.overdueOn", { date })
              : t("sales.nextStep.today"),
        })}
      </StatusChip>
    </span>
  );
}

// ---------------------------------------------------------------------------
// The filters the three pages share
// ---------------------------------------------------------------------------

/**
 * The owner filter and the customer picker (every reader). The owner filter
 * is offered to admins, because only an admin reads the member directory the
 * names come from (`identity.view_members`). An owner already in force, which
 * an address or a shared view carries, is shown to every reader, as
 * "Not in this list" when the directory cannot name it, so no reader has a
 * list narrowed by a filter they cannot see or clear (§4.1's owner filter
 * does the same). The picker is a SEARCH, as on the books: a tenant with
 * hundreds of customers cannot be a Select. The chosen customer lives in the
 * address and is named in words by its own read, never by a loaded row.
 */
export function PipelineFilters({
  owner,
  customer,
  onOwner,
  onCustomer,
  showOwner,
  owners,
  ownersPending,
  testPrefix,
}: {
  readonly owner: string | undefined;
  readonly customer: string | undefined;
  readonly onOwner: (next: string | undefined) => void;
  readonly onCustomer: (next: string | undefined) => void;
  /** Offer the owner filter (a literal admin). An owner in force shows the
   * control whatever this says. */
  readonly showOwner: boolean;
  readonly owners: ReadonlyMap<string, string>;
  readonly ownersPending: boolean;
  /** Test-id prefix, so the three pages' controls stay distinct. */
  readonly testPrefix: string;
}) {
  return (
    <PipelineCustomerFilter
      customer={customer}
      onCustomer={onCustomer}
      testPrefix={testPrefix}
      lead={
        <PipelineOwnerFilter
          owner={owner}
          onOwner={onOwner}
          showOwner={showOwner}
          owners={owners}
          ownersPending={ownersPending}
          testPrefix={testPrefix}
        />
      }
    />
  );
}

/** The owner half of `PipelineFilters`, for a page that puts it behind its
 * Filters button on its own (the deal list). */
export function PipelineOwnerFilter({
  owner,
  onOwner,
  showOwner,
  owners,
  ownersPending,
  testPrefix,
}: {
  readonly owner: string | undefined;
  readonly onOwner: (next: string | undefined) => void;
  readonly showOwner: boolean;
  readonly owners: ReadonlyMap<string, string>;
  readonly ownersPending: boolean;
  readonly testPrefix: string;
}) {
  const { t } = useTranslation();
  if (!showOwner && owner === undefined) return null;
  return (
    <Field label={t("sales.pipeline.filterOwner")}>
      <Select
        value={owner ?? ""}
        data-testid={`${testPrefix}-owner`}
        onChange={(event) => {
          onOwner(event.target.value.length > 0 ? event.target.value : undefined);
        }}
      >
        <option value="">{t("sales.pipeline.anyOwner")}</option>
        {owner !== undefined && !owners.has(owner) ? (
          <option value={owner}>
            {ownersPending ? t("ref.resolving") : t("sales.common.notListed")}
          </option>
        ) : null}
        {[...owners.entries()].map(([id, name]) => (
          <option key={id} value={id}>
            {name}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/** The customer half: the search box, its picker and, unless the page names
 * the chosen customer itself (`chip={false}`, as a filter-bar chip does), the
 * line that names it with Clear. */
export function PipelineCustomerFilter({
  customer,
  onCustomer,
  testPrefix,
  lead,
  chip = true,
}: {
  readonly customer: string | undefined;
  readonly onCustomer: (next: string | undefined) => void;
  readonly testPrefix: string;
  /** Drawn first inside the pickers row (the owner select). */
  readonly lead?: ReactNode;
  readonly chip?: boolean;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const q = useDebouncedValue(text.trim(), 250);
  const matches = useCustomers({ q }, undefined, q.length > 1);
  const chosen = useCustomer360(chip ? (customer ?? "") : "");
  const chosenName = chosen.data?.customer.display_name;

  // Something other than the picker moved the customer (a tab, a link, a
  // view on the list): the box empties so it never contradicts the address.
  const wrote = useRef(customer);
  useEffect(() => {
    if (customer === wrote.current) return;
    wrote.current = customer;
    setText("");
  }, [customer]);
  const writeCustomer = (next: string | undefined) => {
    wrote.current = next;
    onCustomer(next);
  };

  return (
    <>
      <div className="filter-bar" data-testid={`${testPrefix}-pickers`}>
        {lead}
        <Field label={t("sales.pipeline.filterCustomer")}>
          <input
            value={text}
            placeholder={t("sales.pipeline.filterCustomerHint")}
            data-testid={`${testPrefix}-customer-q`}
            onChange={(event) => {
              setText(event.target.value);
              if (customer !== undefined) writeCustomer(undefined);
            }}
          />
        </Field>
        {q.length > 1 ? (
          <Field label={t("sales.pipeline.filterCustomer")}>
            <Select
              value={customer ?? ""}
              data-testid={`${testPrefix}-customer`}
              onChange={(event) => {
                writeCustomer(event.target.value.length > 0 ? event.target.value : undefined);
              }}
            >
              <option value="">{t("sales.pipeline.anyCustomer")}</option>
              {(matches.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
      </div>
      {chip && customer !== undefined ? (
        <div
          className="sales-toolbar"
          data-testid={`${testPrefix}-customer-chip`}
          data-customer={customer}
        >
          <span className="sales-hint">{t("sales.pipeline.filterCustomer")}</span>
          <span data-testid={`${testPrefix}-customer-name`}>
            <RefLabel value={chosenName} id={customer} pending={chosen.isPending} />
          </span>
          <Button
            size="sm"
            data-testid={`${testPrefix}-customer-clear`}
            onClick={() => {
              setText("");
              writeCustomer(undefined);
            }}
          >
            {t("sales.common.filterCustomerClear")}
          </Button>
        </div>
      ) : null}
    </>
  );
}
